import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import {
  PaymentService,
  CreateUpdatePaymentPlanPayload,
  PaymentPlan,
} from "../../api/payment";
import { getAllPatients } from "../../api/patient";
import { getAllTreatmentPlans, TreatmentPlan } from "../../api/treatmentPlan";

const PaymentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id, patientId: routePatientId } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [treatmentPlansLoading, setTreatmentPlansLoading] = useState(false);

  const [formData, setFormData] = useState<CreateUpdatePaymentPlanPayload>({
    id: "",
    treatmentId: "",
    patientId: routePatientId || "",
    doctorId: localStorage.getItem("userId") || "",
    treatmentPlanId: "",
    addComment: "",
    treatmentCost: "",
    discount: "",
    additionalCharges: "",
    reasonForExtraCharge: "",
  });

  const [errors, setErrors] = useState<{
    treatmentId?: string;
    treatmentPlanId?: string;
    patientId?: string;
    treatmentCost?: string;
    discount?: string;
    additionalCharges?: string;
  }>({});

  const [treatmentPlans, setTreatmentPlans] = useState<
    { id: string; label: string; patientId?: string }[]
  >([]);

  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patients, setPatients] = useState<
    {
      id: string;
      name: string;
      clinicId: string;
      clinicName: string;
      treatmentId?: string;
    }[]
  >([]);

  const fetchedTreatmentPlansRef = useRef(false);
  const fetchedPlanRef = useRef(false);

  const goBackToPayments = () => {
    navigate("/payments");
  };

  // Fetch patients
  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((p: any) => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name = p.name || p.fullName || p.label || "";
            const clinicId = p.clinicId || p.clinic?.id || "";
            const clinicName = p.clinic?.name || p.clinicName || "";
            const treatmentId =
              p.treatmentId ||
              p.treatment_id ||
              p.treatment?.id ||
              "";
            return { id, name, clinicId, clinicName, treatmentId };
          })
          .filter((p) => p.id && p.name);
        setPatients(normalized);
      } catch (err) {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  // Fetch treatment plans
  useEffect(() => {
    if (fetchedTreatmentPlansRef.current) return;
    fetchedTreatmentPlansRef.current = true;

    const fetchTreatmentPlans = async () => {
      setTreatmentPlansLoading(true);
      try {
        const plansList = await getAllTreatmentPlans();
        const normalized = plansList.map((plan: TreatmentPlan) => ({
          id: plan.id,
          label: `${plan.type || "Plan"} - ${plan.clinic?.name || ""}`,
          patientId:
            (plan.patientId as string) ||
            (plan as any)?.patientId ||
            (plan as any)?.patient?.id ||
            "",
        }));
        setTreatmentPlans(normalized);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to load treatment plans"
        );
        setTreatmentPlans([]);
      } finally {
        setTreatmentPlansLoading(false);
      }
    };

    fetchTreatmentPlans();
  }, []);

  // When creating from Payments page with patientId in route,
  // auto-attach treatmentId and treatmentPlanId based on that patient
  useEffect(() => {
    if (!routePatientId || id) return; // only for create mode with locked patient
    if (!patients.length && !treatmentPlans.length) return;

    const selected = patients.find((p) => p.id === routePatientId);
    const matchedPlan = treatmentPlans.find(
      (plan) => plan.patientId && plan.patientId === routePatientId
    );

    setFormData((prev) => ({
      ...prev,
      patientId: routePatientId,
      treatmentId: selected?.treatmentId ? String(selected.treatmentId) : "",
      treatmentPlanId: matchedPlan ? matchedPlan.id : "",
    }));
  }, [routePatientId, id, patients, treatmentPlans]);

  // Fetch plan data if editing
  useEffect(() => {
    if (!id || fetchedPlanRef.current) return;
    fetchedPlanRef.current = true;

    const fetchPlan = async () => {
      setLoading(true);
      try {
        const response = await PaymentService.getPlanById(id);
        const plan: PaymentPlan =
          response?.data?.data || response?.data || response;

        if (plan) {
          setFormData({
            id: plan.id,
            treatmentId: plan.treatmentId || "",
            patientId:
              (plan.patientId as string) ||
              plan.patient?.id ||
              "",
            doctorId: plan.doctorId || localStorage.getItem("userId") || "",
            treatmentPlanId: plan.treatmentPlanId || "",
            addComment: plan.addComment || "",
            treatmentCost: plan.treatmentCost || "",
            discount: plan.discount || "",
            additionalCharges: plan.additionalCharges || "",
            reasonForExtraCharge: plan.reasonForExtraCharge || "",
          });
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to load payment plan"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [id]);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.patientId.trim()) {
      newErrors.patientId = "Patient is required";
    }

    // Ensure derived treatment & treatment plan exist before saving
    if (!formData.treatmentId) {
      toast.error(
        "No treatments found for this patient. Please create a treatment first."
      );
      newErrors.treatmentId = "Treatment is required";
    }

    if (!formData.treatmentPlanId) {
      toast.error(
        "No treatment plan found for this patient. Please create a treatment plan first."
      );
      newErrors.treatmentId = newErrors.treatmentId || "Treatment is required";
    }

    if (Number(formData.treatmentCost) < 0) {
      newErrors.treatmentCost = "Treatment cost cannot be negative";
    }

    if (Number(formData.discount) < 0) {
      newErrors.discount = "Discount cannot be negative";
    }

    if (Number(formData.additionalCharges) < 0) {
      newErrors.additionalCharges = "Additional charges cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Ensure `id` is only sent when editing (when route has `id`)
      const { id: formId, ...rest } = formData;
      const payload: CreateUpdatePaymentPlanPayload = {
        ...rest,
        treatmentCost: Number(formData.treatmentCost || 0),
        discount: Number(formData.discount || 0),
        additionalCharges: Number(formData.additionalCharges || 0),
        ...(id && { id: formId || (id as string) }),
      };


      const response = await PaymentService.createOrUpdatePlan(payload);
      const planId = response?.data?.data?.id || response?.data?.id || id;
      toast.success(
        id ? "Payment plan updated successfully!" : "Payment plan created successfully!"
      );
      if (planId) {
        navigate(`/payment/plan/${planId}`);
      } else {
        navigate("/payments");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to save payment plan"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev: CreateUpdatePaymentPlanPayload) => {
      const updated: CreateUpdatePaymentPlanPayload = {
        ...prev,
        [name]:
          name === "treatmentCost" ||
            name === "discount" ||
            name === "additionalCharges"
            ? value === "" ? "" : parseFloat(value)
            : value,

      };

      // When patient changes, auto-populate treatmentId and treatmentPlanId
      if (name === "patientId") {
        const selected = patients.find((p) => p.id === value);
        if (selected?.treatmentId) {
          updated.treatmentId = String(selected.treatmentId);
        } else {
          // If no mapping found, clear treatmentId
          updated.treatmentId = "";
        }

        const matchedPlan = treatmentPlans.find(
          (plan) => plan.patientId && plan.patientId === value
        );
        if (matchedPlan) {
          updated.treatmentPlanId = matchedPlan.id;
        } else {
          updated.treatmentPlanId = "";
        }
      }

      return updated;
    });

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const totalAmount =
    Number(formData.treatmentCost || 0) -
    Number(formData.discount || 0) +
    Number(formData.additionalCharges || 0);


  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      {treatmentPlansLoading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Update Payment Plan" : "Create Payment Plan"}
                </h2>
                <button
                  type="button"
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={goBackToPayments}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >

                {/* Patient */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleChange}
                    required
                    disabled={patientsLoading || (!!routePatientId && !id)}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.patientId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    {patientsLoading ? (
                      <option value="">Loading Patients...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Patient
                        </option>
                        {patients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.patientId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.patientId}
                    </p>
                  )}
                </div>



                {/* Treatment Cost */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Treatment Cost <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="treatmentCost"
                    value={formData.treatmentCost || ""}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    required
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.treatmentCost
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    placeholder="0.00"
                  />
                  {errors.treatmentCost && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.treatmentCost}
                    </p>
                  )}
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Discount
                  </label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount || ""}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.discount
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    placeholder="0.00"
                  />
                  {errors.discount && (
                    <p className="text-red-500 text-sm mt-1">{errors.discount}</p>
                  )}
                </div>

                {/* Additional Charges */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Additional Charges
                  </label>
                  <input
                    type="number"
                    name="additionalCharges"
                    value={formData.additionalCharges || ""}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.additionalCharges
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                    placeholder="0.00"
                  />
                  {errors.additionalCharges && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.additionalCharges}
                    </p>
                  )}
                </div>

                {/* Reason for Extra Charge */}
                {Number(formData.additionalCharges) > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600">
                      Reason for Extra Charge
                    </label>
                    <input
                      type="text"
                      name="reasonForExtraCharge"
                      value={formData.reasonForExtraCharge}
                      onChange={handleChange}
                      className="mt-1 w-full p-2 border rounded-xl"
                      placeholder="Enter reason for additional charges"
                    />
                  </div>
                )}

                {/* Total Amount Display */}
                <div className="md:col-span-2 bg-gray-50 p-4 rounded-xl mt-2">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Billing Summary
                      </p>
                      <p className="text-xs text-gray-500">
                        Automatically calculated from cost, discount and extra charges.
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="block text-sm font-semibold text-gray-700">
                        Total Amount
                      </span>
                      <span className="text-2xl font-bold text-[#016683]">
                        ₹{totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 text-sm text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Treatment Cost</span>
                      <span>₹{Number(formData.treatmentCost || 0).toFixed(2)}</span>
                    </div>
                    {Number(formData.discount) > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Discount</span>
                        <span>-₹{Number(formData.discount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(formData.additionalCharges) > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Additional Charges</span>
                        <span>+₹{Number(formData.additionalCharges || 0).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comment */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-600">
                    Comments
                  </label>
                  <textarea
                    name="addComment"
                    value={formData.addComment}
                    onChange={handleChange}
                    rows={4}
                    className="mt-1 w-full p-2 border rounded-xl"
                    placeholder="Add any additional comments about this payment plan..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="md:col-span-2 flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={goBackToPayments}
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-[#016683] text-white hover:opacity-90 transition"
                  >
                    {id ? "Update Plan" : "Create Plan"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PaymentForm;

